/**
 * React App Root Component
 * Small Manufacturing ERP - Master Data Management
 */
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { ConfigProvider, App as AntdApp } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { NotificationProvider } from './shared/contexts/NotificationContext';
import { MainLayout } from './shared/components/MainLayout';
import { ErrorBoundary } from './shared/components/ErrorBoundary';
import { PageLoading } from './shared/components/PageLoading';
import { ProtectedRoute, RoleRoute } from './shared/components/ProtectedRoute';
import { getRolesForRoute, getDefaultRouteForRole } from './shared/config/role-routes';
import { getRolesForReportRoute } from './features/reports/config/report-access';
import { UnauthorizedPage } from './shared/components/UnauthorizedPage';
import { LoginPage } from './features/auth/pages/LoginPage';
import { useAuthStore } from './features/auth/stores/authStore';
import { useIsMobile } from './shared/hooks/useBreakpoint';
import { buildAppTheme } from './shared/theme/appTheme';
import { type EmployeeRole, EMPLOYEE_ROLES } from './shared/types/employee.types';

/** Set of valid EmployeeRole values for O(1) runtime validation */
const VALID_ROLES = new Set<string>(EMPLOYEE_ROLES);

// Lazy load all page components for code splitting
// Master Data pages
const MaterialsPage = lazy(() => import('./features/master-data/pages/MaterialsPage').then(m => ({ default: m.MaterialsPage })));
const CustomersPage = lazy(() => import('./features/master-data/pages/CustomersPage').then(m => ({ default: m.CustomersPage })));
const SuppliersPage = lazy(() => import('./features/master-data/pages/SuppliersPage').then(m => ({ default: m.SuppliersPage })));
const WarehousesPage = lazy(() => import('./features/master-data/pages/WarehousesPage').then(m => ({ default: m.WarehousesPage })));
const DepartmentsPage = lazy(() => import('./features/master-data/pages/DepartmentsPage').then(m => ({ default: m.DepartmentsPage })));
const EmployeesPage = lazy(() => import('./features/master-data/pages/EmployeesPage').then(m => ({ default: m.EmployeesPage })));
const CategoriesPage = lazy(() => import('./features/master-data/pages/CategoriesPage').then(m => ({ default: m.CategoriesPage })));
const UomReferencePage = lazy(() => import('./features/master-data/pages/UomReferencePage').then(m => ({ default: m.UomReferencePage })));

// Inventory Management pages
const InboundDocumentsPage = lazy(() => import('./features/inventory-management').then(m => ({ default: m.InboundDocumentsPage })));
const OutboundDocumentsPage = lazy(() => import('./features/inventory-management').then(m => ({ default: m.OutboundDocumentsPage })));
const CountingDocumentsPage = lazy(() => import('./features/inventory-management').then(m => ({ default: m.CountingDocumentsPage })));
const InventoryAlertsPage = lazy(() => import('./features/inventory-management').then(m => ({ default: m.InventoryAlertsPage })));
const InventoryBalancePage = lazy(() => import('./features/inventory-management').then(m => ({ default: m.InventoryBalancePage })));

// Sales Management pages
const SalesOrdersPage = lazy(() => import('./features/sales-management').then(m => ({ default: m.SalesOrdersPage })));
const ShipmentsPage = lazy(() => import('./features/sales-management').then(m => ({ default: m.ShipmentsPage })));
const QuotationsPage = lazy(() => import('./features/sales-management').then(m => ({ default: m.QuotationsPage })));
const SpecialPriceRequestsPage = lazy(() => import('./features/sales-management').then(m => ({ default: m.SpecialPriceRequestsPage })));
const PriceListsPage = lazy(() => import('./features/sales-management').then(m => ({ default: m.PriceListsPage })));
const ReturnsPage = lazy(() => import('./features/sales-management').then(m => ({ default: m.ReturnsPage })));
const StatementsPage = lazy(() => import('./features/sales-management').then(m => ({ default: m.StatementsPage })));

// Procurement Management pages
const PurchaseOrdersPage = lazy(() => import('./features/procurement').then(m => ({ default: m.PurchaseOrdersPage })));
const PurchaseReceiptsPage = lazy(() => import('./features/procurement').then(m => ({ default: m.PurchaseReceiptsPage })));
const PurchaseReturnsPage = lazy(() => import('./features/procurement').then(m => ({ default: m.PurchaseReturnsPage })));

// Production Management pages
const ProductionDashboardPage = lazy(() => import('./features/production').then(m => ({ default: m.ProductionDashboardPage })));
const BOMListPage = lazy(() => import('./features/production').then(m => ({ default: m.BOMListPage })));
const WorkOrdersPage = lazy(() => import('./features/production').then(m => ({ default: m.WorkOrdersPage })));

// Finance Management pages
const ReceivablesPage = lazy(() => import('./features/finance-management').then(m => ({ default: m.ReceivablesPage })));
const PayablesPage = lazy(() => import('./features/finance-management').then(m => ({ default: m.PayablesPage })));
const ReceiptVouchersPage = lazy(() => import('./features/finance-management').then(m => ({ default: m.ReceiptVouchersPage })));
const PaymentVouchersPage = lazy(() => import('./features/finance-management').then(m => ({ default: m.PaymentVouchersPage })));
const AgingReportsPage = lazy(() => import('./features/finance-management').then(m => ({ default: m.AgingReportsPage })));

// Reports & Analytics pages
const ReportsDashboard = lazy(() => import('./features/reports').then(m => ({ default: m.ReportsDashboard })));
const InventoryReportPage = lazy(() => import('./features/reports').then(m => ({ default: m.InventoryReportPage })));
const SalesReportPage = lazy(() => import('./features/reports').then(m => ({ default: m.SalesReportPage })));
const FulfillmentReportPage = lazy(() => import('./features/reports').then(m => ({ default: m.FulfillmentReportPage })));
const ProductionReportPage = lazy(() => import('./features/reports').then(m => ({ default: m.ProductionReportPage })));
const APAgingReportPage = lazy(() => import('./features/reports').then(m => ({ default: m.APAgingReportPage })));
const ARAgingReportPage = lazy(() => import('./features/reports').then(m => ({ default: m.ARAgingReportPage })));

// Documents module 9: standardized print output
const TemplateListPage = lazy(() => import('./features/documents/pages/TemplateListPage'));
const TemplateDesignerPage = lazy(() => import('./features/documents/pages/TemplateDesignerPage'));
const PrintPreviewPage = lazy(() => import('./features/documents/pages/PrintPreviewPage'));


/**
 * Role-aware redirect component.
 * Redirects authenticated users to their default landing route based on role.
 * Falls back to /login for unauthenticated users or users with invalid roles.
 */
function RoleAwareRedirect() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    // Not authenticated - redirect to login
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    // Authenticated but user data not loaded yet - wait for hydration
    if (!user?.role) {
      return;
    }

    // Validate role before using to prevent issues with tampered JWTs or invalid data
    if (VALID_ROLES.has(user.role)) {
      const defaultRoute = getDefaultRouteForRole(user.role as EmployeeRole);
      navigate(defaultRoute, { replace: true });
    } else {
      // Invalid role - treat as unauthenticated
      navigate('/login', { replace: true });
    }
  }, [navigate, user?.role, isAuthenticated]);

  return <PageLoading />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Public route - Login */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected routes - require authentication */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Default route - redirect to role-aware landing page */}
          <Route index element={<RoleAwareRedirect />} />

          {/* Master Data routes */}
          <Route
            path="materials"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/materials')}>
                <MaterialsPage />
              </RoleRoute>
            }
          />
          <Route
            path="customers"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/customers')}>
                <CustomersPage />
              </RoleRoute>
            }
          />
          <Route
            path="suppliers"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/suppliers')}>
                <SuppliersPage />
              </RoleRoute>
            }
          />
          <Route
            path="warehouses"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/warehouses')}>
                <WarehousesPage />
              </RoleRoute>
            }
          />
          <Route
            path="departments"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/departments')}>
                <DepartmentsPage />
              </RoleRoute>
            }
          />
          <Route
            path="employees"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/employees')}>
                <EmployeesPage />
              </RoleRoute>
            }
          />
          <Route
            path="categories"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/categories')}>
                <CategoriesPage />
              </RoleRoute>
            }
          />
          <Route
            path="units-of-measure"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/units-of-measure')}>
                <UomReferencePage />
              </RoleRoute>
            }
          />

          {/* Inventory Management routes */}
          <Route
            path="inbound-documents"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/inbound-documents')}>
                <InboundDocumentsPage />
              </RoleRoute>
            }
          />
          <Route
            path="outbound-documents"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/outbound-documents')}>
                <OutboundDocumentsPage />
              </RoleRoute>
            }
          />
          <Route
            path="counting-documents"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/counting-documents')}>
                <CountingDocumentsPage />
              </RoleRoute>
            }
          />
          <Route
            path="inventory-alerts"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/inventory-alerts')}>
                <InventoryAlertsPage />
              </RoleRoute>
            }
          />
          <Route
            path="inventory-balances"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/inventory-balances')}>
                <InventoryBalancePage />
              </RoleRoute>
            }
          />

          {/* Sales Management routes */}
          <Route
            path="sales-orders"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/sales-orders')}>
                <SalesOrdersPage />
              </RoleRoute>
            }
          />
          <Route
            path="shipments"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/shipments')}>
                <ShipmentsPage />
              </RoleRoute>
            }
          />
          <Route
            path="quotations"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/quotations')}>
                <QuotationsPage />
              </RoleRoute>
            }
          />
          <Route
            path="price-lists"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/price-lists')}>
                <PriceListsPage />
              </RoleRoute>
            }
          />
          <Route
            path="special-price-requests"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/special-price-requests')}>
                <SpecialPriceRequestsPage />
              </RoleRoute>
            }
          />
          <Route
            path="returns"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/returns')}>
                <ReturnsPage />
              </RoleRoute>
            }
          />
          <Route
            path="statements"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/statements')}>
                <StatementsPage />
              </RoleRoute>
            }
          />

          {/* Procurement Management routes */}
          <Route
            path="purchase-orders"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/purchase-orders')}>
                <PurchaseOrdersPage />
              </RoleRoute>
            }
          />
          <Route
            path="purchase-receipts"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/purchase-receipts')}>
                <PurchaseReceiptsPage />
              </RoleRoute>
            }
          />
          <Route
            path="purchase-returns"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/purchase-returns')}>
                <PurchaseReturnsPage />
              </RoleRoute>
            }
          />

          {/* Production Management routes */}
          <Route
            path="production"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/production')}>
                <ProductionDashboardPage />
              </RoleRoute>
            }
          />
          <Route
            path="production/work-orders"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/production/work-orders')}>
                <WorkOrdersPage />
              </RoleRoute>
            }
          />
          <Route
            path="production/boms"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/production/boms')}>
                <BOMListPage />
              </RoleRoute>
            }
          />

          {/* Finance Management routes */}
          <Route
            path="receivables"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/receivables')}>
                <ReceivablesPage />
              </RoleRoute>
            }
          />
          <Route
            path="payables"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/payables')}>
                <PayablesPage />
              </RoleRoute>
            }
          />
          <Route
            path="receipt-vouchers"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/receipt-vouchers')}>
                <ReceiptVouchersPage />
              </RoleRoute>
            }
          />
          <Route
            path="payment-vouchers"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/payment-vouchers')}>
                <PaymentVouchersPage />
              </RoleRoute>
            }
          />
          <Route
            path="aging-reports"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/aging-reports')}>
                <AgingReportsPage />
              </RoleRoute>
            }
          />

          {/* Reports & Analytics routes */}
          <Route
            path="reports"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/reports')}>
                <ReportsDashboard />
              </RoleRoute>
            }
          />
          <Route
            path="reports/inventory"
            element={
              <RoleRoute allowedRoles={getRolesForReportRoute('/reports/inventory')}>
                <InventoryReportPage />
              </RoleRoute>
            }
          />
          <Route
            path="reports/sales"
            element={
              <RoleRoute allowedRoles={getRolesForReportRoute('/reports/sales')}>
                <SalesReportPage />
              </RoleRoute>
            }
          />
          <Route
            path="reports/fulfillment"
            element={
              <RoleRoute allowedRoles={getRolesForReportRoute('/reports/fulfillment')}>
                <FulfillmentReportPage />
              </RoleRoute>
            }
          />
          <Route
            path="reports/production"
            element={
              <RoleRoute allowedRoles={getRolesForReportRoute('/reports/production')}>
                <ProductionReportPage />
              </RoleRoute>
            }
          />
          <Route
            path="reports/aging/ap"
            element={
              <RoleRoute allowedRoles={getRolesForReportRoute('/reports/aging/ap')}>
                <APAgingReportPage />
              </RoleRoute>
            }
          />
          <Route
            path="reports/aging/ar"
            element={
              <RoleRoute allowedRoles={getRolesForReportRoute('/reports/aging/ar')}>
                <ARAgingReportPage />
              </RoleRoute>
            }
          />


          {/* Documents module 9: standardized print output */}
          <Route
            path="documents/templates"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/documents/templates')}>
                <TemplateListPage />
              </RoleRoute>
            }
          />
          <Route
            path="documents/templates/new"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/documents/templates/new')}>
                <TemplateDesignerPage />
              </RoleRoute>
            }
          />
          <Route
            path="documents/templates/:id/edit"
            element={
              <RoleRoute allowedRoles={getRolesForRoute('/documents/templates')}>
                <TemplateDesignerPage />
              </RoleRoute>
            }
          />

          {/* 403 Unauthorized page - accessible to all authenticated users */}
          <Route path="unauthorized" element={<UnauthorizedPage />} />

          {/* Catch all - redirect to role-aware landing page */}
          <Route path="*" element={<RoleAwareRedirect />} />
        </Route>

        {/* Print preview (module 9): authenticated and role-gated like the
            in-app routes, but chrome-less — no MainLayout/sidebar. The
            preview is a pure pre-print step opened in its own tab and must
            not expose app navigation alongside the document. */}
        <Route
          path="/print/:docType/:id"
          element={
            <RoleRoute allowedRoles={getRolesForRoute('/print/sales-order')}>
              <PrintPreviewPage />
            </RoleRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

function App() {
  const { i18n } = useTranslation();
  const antdLocale = i18n.language === 'en-US' ? enUS : zhCN;
  // Intent-20 goal-157 m2: raise control height to the 44px touch-target floor
  // on mobile (320–767px) while preserving the existing 36px desktop posture.
  const isMobile = useIsMobile();

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={buildAppTheme(isMobile)}
    >
      <AntdApp>
        <ErrorBoundary>
          <NotificationProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </NotificationProvider>
        </ErrorBoundary>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
